// frontend/modules/template/components/ContextScriptList.tsx
import React, { useState } from 'react';
import { 
    Table, 
    Tag, 
    Button, 
    Space, 
    Popconfirm, 
    Badge, 
    Typography, 
    Tooltip
} from 'antd';
import { 
    EditOutlined, 
    DeleteOutlined, 
    EyeOutlined, 
    CopyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {ContextScriptDTO} from "@/shared/api/model";

const { Text } = Typography;

interface ContextScriptListProps {
    scripts: ContextScriptDTO[];
    loading?: boolean;
    onDelete?: (id?: string) => void;
}

export const ContextScriptList: React.FC<ContextScriptListProps> = ({ 
    scripts,
    loading = false,
    onDelete
}) => {
    const navigate = useNavigate();
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    
    const handleEdit = (id?: string) => {
        if (!id) return;
        navigate(`/notifications/context-scripts/edit/${id}`);
    };
    
    const handleView = (id?: string) => {
        if (!id) return;
        navigate(`/notifications/context-scripts/view/${id}`);
    };
    
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: ContextScriptDTO) => (
                <Space>
                    <Text 
                        strong 
                        style={{ cursor: 'pointer' }} 
                        onClick={() => handleView(record.id)}
                    >
                        {text}
                    </Text>
                    {!record.active && (
                        <Badge status="default" text="Inactive" />
                    )}
                </Space>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
        },
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: {
                showTitle: false,
            },
            render: (description: string) => (
                description ? (
                    <Tooltip title={description}>
                        <span>{description}</span>
                    </Tooltip>
                ) : '-'
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: ContextScriptDTO) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record.id)}
                    />
                    <Button 
                        type="text" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record.id)}
                    />
                    {onDelete && (
                        <Popconfirm
                            title="Are you sure you want to delete this script?"
                            onConfirm={() => onDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                            />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];
    
    const handleRowExpand = (expanded: boolean, record: ContextScriptDTO) => {
        const keys = [...expandedRowKeys];
        if (!record.id) return;
        if (expanded) {
            keys.push(record.id);
        } else {
            const index = keys.indexOf(record.id);
            if (index !== -1) {
                keys.splice(index, 1);
            }
        }
        setExpandedRowKeys(keys);
    };
    
    return (
        <Table
            columns={columns}
            dataSource={scripts}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            expandable={{
                expandedRowKeys,
                onExpand: handleRowExpand,
                expandedRowRender: (record) => (
                    <div>
                        <Typography.Title level={5}>Sample Input/Output</Typography.Title>
                        {record.sampleInput ? (
                            <div className="mb-4">
                                <Typography.Text strong>Sample Input:</Typography.Text>
                                <pre className="bg-gray-50 p-4 rounded mt-2 overflow-auto">
                                    {record.sampleInput}
                                </pre>
                            </div>
                        ) : (
                            <Typography.Text type="secondary">No sample input provided</Typography.Text>
                        )}
                        
                        {record.expectedOutput && (
                            <div className="mt-4">
                                <Typography.Text strong>Expected Output:</Typography.Text>
                                <pre className="bg-gray-50 p-4 rounded mt-2 overflow-auto">
                                    {record.expectedOutput}
                                </pre>
                            </div>
                        )}
                    </div>
                ),
            }}
        />
    );
};
